import {useEffect, useRef, useState} from 'react'
import _ from 'lodash'
// Components
import FaIconLibrary from '../../icons/fa-icon-library'
// Types
import type {CSSProperties, SyntheticEvent} from 'react'
import type {Ballot, BallotDisplayType} from '../../../types/ballot'
import type {GalleryEntry, PreviousVote} from '../../../types/photo-gallery'
// Styles
import styles from './photo-flow-entry.module.scss'

type FlowEntryVariant = 'grid' | 'masonry'

interface PhotoFlowEntryProps {
  variant: FlowEntryVariant
  ballot: Ballot | null
  question?: any
  entry: GalleryEntry
  rankDictionary: Record<number, string> | null
  previousVote?: PreviousVote | null
  ballotDisplayType: BallotDisplayType | null | undefined
  isSelected: boolean
  /** Grid-specific: gap between entries */
  gridGap?: number
  /** Grid-specific: current window width for column calculation */
  currentWindowWidth?: number
  /** Grid-specific: container styles shared across entries */
  gridContainerStyles?: CSSProperties | null
  /** Grid-specific: callback to send computed container styles */
  sendGridContainerStyles?: ((value: CSSProperties) => void) | null
  /** Masonry-specific: gap between entries */
  masonryGap?: number
  onSelectedAnswerChange: ((syntheticEvent: SyntheticEvent, entry: GalleryEntry) => void) | null
  onOpenEntryImagesModal: (entry: GalleryEntry) => void
}

const PhotoFlowEntry = ({
  variant,
  ballot = null,
  entry = {} as GalleryEntry,
  rankDictionary = null,
  ballotDisplayType = null,
  isSelected = false,
  gridGap = 12,
  currentWindowWidth = 0,
  gridContainerStyles = null,
  sendGridContainerStyles = null,
  masonryGap = 12,
  onSelectedAnswerChange = null,
  onOpenEntryImagesModal,
}: PhotoFlowEntryProps) => {
  const [flowEntry, setFlowEntry] = useState<GalleryEntry | null>(null)

  // Grid-specific state
  const [aspectRatio, setAspectRatio] = useState<number[]>([])
  const entryRef = useRef<HTMLDivElement>(null)
  const [gridEntryStyles, setGridEntryStyles] = useState<CSSProperties>({})
  const [gridImgStyles, setGridImgStyles] = useState<CSSProperties>({})

  const setComponentState = (entry: GalleryEntry) => {
    const rankTextDictionary = rankDictionary
    const isRest = _.isEmpty(rankTextDictionary?.[entry.rank!]) || ballotDisplayType === 'photo-voting-finalist-announcement'
    const theRestRank = _.last(_.values(rankTextDictionary))
    const newRankIndex = Math.min(entry.rank ?? 0, _.values(rankTextDictionary)?.length)
    const newRankDisplayText = isRest ? theRestRank : rankTextDictionary?.[newRankIndex] || theRestRank

    let newFlowEntry = _.clone(entry)
    const optimizedCoverImage = `${entry.cover_image}-/resize/x799/-/quality/smart/-/progressive/yes/`
    newFlowEntry['cover_image'] = optimizedCoverImage

    newFlowEntry['rank_display_text'] = newRankDisplayText
    newFlowEntry['rank_index'] = newRankIndex

    newFlowEntry['top_level_classes'] = `${styles['photo-flow-entry']} ${styles[`--${variant}`]}`
    if (isSelected) {
      newFlowEntry['top_level_classes'] += ` ${styles['--is-selected']}`
    }
    if (entry.type === 'submission') {
      newFlowEntry['top_level_classes'] += ` ${styles['--is-submission']}`
    } else {
      newFlowEntry['top_level_classes'] += ` ${styles['--is-entry']}`
    }
    if (newRankIndex < 3) {
      newFlowEntry['top_level_classes'] += ` ${styles['--' + newRankIndex.toString()]}`
    }
    setFlowEntry(newFlowEntry)
  }

  useEffect(() => {
    if (variant === 'grid' && ballot) {
      const newAspectRatio = _.map(_.split((ballot?.photo_submission_settings as any)?.aspect_ratio || '3:2', ':'), _.toNumber)
      setAspectRatio(newAspectRatio)
    }
    setComponentState(entry)
  }, [entry])

  // Grid-specific: calculate column/image sizing
  useEffect(() => {
    if (variant !== 'grid' || !currentWindowWidth) return

    const minWidth = 400
    const columnCount = Math.floor(currentWindowWidth / minWidth)
    const windowWidthMinusPadding = currentWindowWidth - gridGap - (gridGap * columnCount)
    const columnWidth = windowWidthMinusPadding / columnCount
    const entryWidth = columnWidth - 2
    const heightMultiplier = _.last(aspectRatio)! / _.first(aspectRatio)!
    const entryHeight = Math.round(columnWidth * heightMultiplier) || 100
    const newGridContainerStyles: CSSProperties = currentWindowWidth <= minWidth
      ? {width: currentWindowWidth - (2 * (gridGap)) - 2}
      : {
        width: entryWidth - 2,
        height: entryHeight,
      }
    sendGridContainerStyles?.(newGridContainerStyles)

    const entryIsTall = entryHeight > entryWidth
    let newGridImgStyles: CSSProperties = {aspectRatio: `${_.first(aspectRatio)} / ${_.last(aspectRatio)}`}
    newGridImgStyles['height'] = entryIsTall ? '100%' : 'auto'
    newGridImgStyles['width'] = entryIsTall ? 'auto' : '100%'
    newGridImgStyles['objectFit'] = 'cover'
    setGridImgStyles(newGridImgStyles)

    const newGridEntryStyles: CSSProperties = currentWindowWidth <= 400 ? {} : {maxWidth: entryWidth}
    setGridEntryStyles(newGridEntryStyles)
  }, [
    entryRef,
    aspectRatio,
    currentWindowWidth,
  ])

  const gap = variant === 'grid' ? gridGap : masonryGap
  const variantClass = styles[`--${variant}`] || ''
  const isMasonryLike = variant === 'masonry'
  const containerStyleProp = isMasonryLike ? {marginBottom: `${gap}px`} : gridEntryStyles

  return flowEntry
    ? <>
      {_.includes(['photo-voting-vote', 'photo-submissions-submit'], ballotDisplayType) && <>
        <div
          key={flowEntry.key}
          ref={entryRef}
          className={`
            ${styles['photo-flow-entry']}
            ${variantClass}
            ${isSelected ? styles['--is-selected'] : ''}
            ${flowEntry.type === 'submission' ? styles['--is-submission'] : styles['--is-entry']}
          `}
          style={containerStyleProp}
        >
          {flowEntry.type === 'option' && <>
            <div
              className={styles['__image-container']}
              style={variant === 'grid' ? (gridContainerStyles || undefined) : undefined}
            >
              <img
                role="button"
                aria-label={flowEntry.photo_name || ''}
                src={flowEntry.cover_image || ''}
                onClick={() => onOpenEntryImagesModal(flowEntry)}
                className={styles['__image']}
                style={variant === 'grid' ? gridImgStyles : undefined}
              />
              <div className={`buttons ${styles['__buttons-container']}`}>
                <button
                  type='button'
                  onClick={() => onOpenEntryImagesModal(flowEntry)}
                  className={`button ${styles['__button-on-image']}`}
                >
                  {(flowEntry.photo_images_array?.length ?? 0) > 1 ? 'View All Photos' : 'View Full Image'}
                </button>
              </div>
            </div>
            <div className={styles['__footer']}>
              <div className={styles['__title']}>{flowEntry.photo_name}</div>
              <div className={`buttons is-right ${styles['__buttons']}`}>
                {isSelected &&
                  <div className={styles['__selected-vote-icon']}>
                    <FaIconLibrary icon='check-to-slot' size='2x' />
                  </div>
                }
                {!isSelected &&
                  <button
                    type='button'
                    onClick={syntheticEvent => onSelectedAnswerChange?.(syntheticEvent, flowEntry)}
                    className='button is-dark'
                  >
                    Vote
                  </button>
                }
              </div>
            </div>
          </>}
          {flowEntry.type === 'submission' && <>
            <div
              className={styles['__image-container']}
              style={variant === 'grid' ? (gridContainerStyles || undefined) : undefined}
            >
              <div className={styles['__overlay']}>
                <div className={styles['__overlay-text']}>Once approved, your submission will appear here</div>
              </div>
              <img
                aria-label={flowEntry.photo_name || ''}
                src={flowEntry.cover_image || ''}
                className={styles['__image']}
              />
            </div>
            <div className={styles['__footer']}>
              <div className={styles['__title']}>{flowEntry.photo_name}</div>
              <div className={`buttons is-right ${styles['__buttons']}`}>
                <button className='button is-dark is-large'>Continue ▼</button>
              </div>
            </div>
          </>}
        </div>
      </>}
      {_.includes(['photo-voting-results', 'photo-voting-finalist-announcement'], ballotDisplayType) && <>
        {flowEntry.type === 'option' && <>
          <div
            key={flowEntry.key}
            ref={entryRef}
            className={`
              ${flowEntry.top_level_classes}
              ${isSelected ? styles['--is-selected'] : ''}
            `}
            style={containerStyleProp}
          >
            <div
              className={styles['__image-container']}
              style={variant === 'grid' ? (gridContainerStyles || undefined) : undefined}
            >
              <img
                aria-label={flowEntry.photo_name || ''}
                src={flowEntry.cover_image || ''}
                alt={flowEntry.photo_name || undefined}
                onClick={() => onOpenEntryImagesModal(flowEntry)}
                className={`${styles['__image']} ${isMasonryLike ? styles['--ranked-display'] : ''}`}
                style={variant === 'grid' ? gridImgStyles : undefined}
              />
            </div>
            <div className={styles['__footer']}>
              <div className={styles['__title']}>{flowEntry.photo_name}</div>
              <div className={styles['__rank-display']}>{flowEntry.rank_display_text}</div>
            </div>
          </div>
        </>}
      </>}
    </>
    : null
}

export default PhotoFlowEntry
